import {
  collection,
  doc,
  getDoc,
  getDocs,
  query,
  serverTimestamp,
  setDoc,
  where,
  type Timestamp,
} from 'firebase/firestore';
import { appConfig } from '@/config/app';
import { getCommercialOperation } from '@/modules/commercial/services/commercialOperationsService';
import { BILLABLE_OPERATION_STATUSES } from '@/modules/commercial/types';
import {
  BILLING_COUNTERS_COLLECTION,
  INVOICES_COLLECTION,
} from '@/modules/billing/services/constants';
import type { Invoice, InvoiceInput, InvoiceStatus } from '@/modules/billing/types';
import { buildInvoiceInputFromOperation } from '@/modules/billing/utils/invoiceFromOperation';
import {
  formatInternalInvoiceNumber,
  maxInternalSequenceFromNumbers,
} from '@/modules/billing/utils/invoiceNumbering';
import { validateInvoiceInput } from '@/modules/billing/utils/validation';
import {
  buildTimestampPayload,
  timestampToDate,
} from '@/modules/inventory/services/firestoreMapper';
import { getFirestoreDb } from '@/services/firebase';
import {
  publishInvoiceCreated,
  publishInvoicePending,
} from '@/services/events/eventPublisher.service';
import { recordEntityWrite, toAuditSnapshot } from '@/services/audit';
import { withFirestoreError } from '@/shared/utils/firestoreError';

type InvoiceFirestoreDoc = {
  companyId: string;
  commercialOperationId: string | null;
  clientId: string | null;
  type: Invoice['type'];
  status: InvoiceStatus;
  invoiceNumber: string;
  issueDate: string;
  dueDate: string;
  subtotal: number;
  taxes: number;
  total: number;
  currency: string;
  notes: string;
  arcaStatus: string;
  arcaCAE: string;
  arcaCAEExpiration: string;
  createdBy: string;
  createdAt: Timestamp;
  updatedAt: Timestamp;
};

function mapInvoice(id: string, data: InvoiceFirestoreDoc): Invoice {
  return {
    id,
    companyId: data.companyId,
    commercialOperationId: data.commercialOperationId,
    clientId: data.clientId,
    type: data.type,
    status: data.status,
    invoiceNumber: data.invoiceNumber ?? '',
    issueDate: data.issueDate ?? '',
    dueDate: data.dueDate ?? '',
    subtotal: data.subtotal ?? 0,
    taxes: data.taxes ?? 0,
    total: data.total ?? 0,
    currency: data.currency ?? 'ARS',
    notes: data.notes ?? '',
    arcaStatus: data.arcaStatus ?? '',
    arcaCAE: data.arcaCAE ?? '',
    arcaCAEExpiration: data.arcaCAEExpiration ?? '',
    createdBy: data.createdBy,
    createdAt: timestampToDate(data.createdAt),
    updatedAt: timestampToDate(data.updatedAt),
  };
}

function computeTotal(input: InvoiceInput): number {
  if (input.total > 0) return input.total;
  return Math.round((input.subtotal + input.taxes) * 100) / 100;
}

async function listAllInvoices(
  companyId: string = appConfig.companyId,
): Promise<Invoice[]> {
  const q = query(
    collection(getFirestoreDb(), INVOICES_COLLECTION),
    where('companyId', '==', companyId),
  );
  const snap = await getDocs(q);
  return snap.docs.map((d) =>
    mapInvoice(d.id, d.data() as InvoiceFirestoreDoc),
  );
}

async function allocateInternalInvoiceNumber(
  companyId: string = appConfig.companyId,
): Promise<string> {
  const counterRef = doc(
    getFirestoreDb(),
    BILLING_COUNTERS_COLLECTION,
    companyId,
  );
  const counterSnap = await getDoc(counterRef);
  let nextSeq = 1;
  if (counterSnap.exists()) {
    const stored = counterSnap.data().lastInternalSeq;
    if (typeof stored === 'number' && stored >= 0) {
      nextSeq = stored + 1;
    }
  } else {
    const all = await listAllInvoices(companyId);
    const max = maxInternalSequenceFromNumbers(
      all.map((i) => i.invoiceNumber).filter(Boolean),
    );
    nextSeq = max + 1;
  }
  await setDoc(
    counterRef,
    { companyId, lastInternalSeq: nextSeq, updatedAt: serverTimestamp() },
    { merge: true },
  );
  return formatInternalInvoiceNumber(nextSeq);
}

function shouldAssignNumber(
  status: InvoiceStatus,
  existingNumber: string,
): boolean {
  return (
    !existingNumber &&
    (status === 'issued' || status === 'pending')
  );
}

export async function listInvoices(
  companyId: string = appConfig.companyId,
): Promise<Invoice[]> {
  const rows = await listAllInvoices(companyId);
  return rows.sort((a, b) => b.updatedAt.getTime() - a.updatedAt.getTime());
}

export async function listInvoicesForOperation(
  commercialOperationId: string,
): Promise<Invoice[]> {
  const all = await listInvoices();
  return all.filter((i) => i.commercialOperationId === commercialOperationId);
}

export async function getInvoice(id: string): Promise<Invoice | null> {
  const snap = await getDoc(doc(getFirestoreDb(), INVOICES_COLLECTION, id));
  if (!snap.exists()) return null;
  return mapInvoice(snap.id, snap.data() as InvoiceFirestoreDoc);
}

export async function saveInvoice(
  input: InvoiceInput,
  createdBy: string,
  id?: string,
): Promise<Invoice> {
  return withFirestoreError(async () => {
  const validation = validateInvoiceInput(input);
  if (validation) throw new Error(validation);

  const ref = id
    ? doc(getFirestoreDb(), INVOICES_COLLECTION, id)
    : doc(collection(getFirestoreDb(), INVOICES_COLLECTION));
  const existing = await getDoc(ref);
  const prev = existing.exists()
    ? mapInvoice(existing.id, existing.data() as InvoiceFirestoreDoc)
    : null;
  const before = prev ? toAuditSnapshot(prev) : undefined;

  let invoiceNumber = prev?.invoiceNumber ?? '';
  if (shouldAssignNumber(input.status, invoiceNumber)) {
    invoiceNumber = await allocateInternalInvoiceNumber();
  }

  const total = computeTotal(input);
  const payload = {
    companyId: appConfig.companyId,
    commercialOperationId: input.commercialOperationId,
    clientId: input.clientId,
    type: input.type,
    status: input.status,
    invoiceNumber,
    issueDate: input.issueDate,
    dueDate: input.dueDate,
    subtotal: Math.max(0, input.subtotal),
    taxes: Math.max(0, input.taxes),
    total,
    currency: input.currency || 'ARS',
    notes: input.notes.trim(),
    arcaStatus: prev?.arcaStatus ?? '',
    arcaCAE: prev?.arcaCAE ?? '',
    arcaCAEExpiration: prev?.arcaCAEExpiration ?? '',
    createdBy: existing.exists()
      ? (existing.data() as InvoiceFirestoreDoc).createdBy
      : createdBy,
    ...buildTimestampPayload(!existing.exists()),
  };
  await setDoc(ref, payload, { merge: true });
  const saved = await getDoc(ref);
  if (!saved.exists()) throw new Error('No se pudo guardar la factura.');
  const result = mapInvoice(saved.id, saved.data() as InvoiceFirestoreDoc);
  const isCreate = !existing.exists();
  recordEntityWrite({
    entityType: 'invoice',
    entityId: result.id,
    isCreate,
    ...(before !== undefined ? { before } : {}),
    after: toAuditSnapshot(result, ['arcaCAE', 'arcaCAEExpiration']),
  });
  if (isCreate) {
    void publishInvoiceCreated({
      invoiceId: result.id,
      status: result.status,
      total: result.total,
      currency: result.currency,
    });
    if (result.status === 'pending') {
      void publishInvoicePending({
        invoiceId: result.id,
        status: result.status,
        total: result.total,
        currency: result.currency,
      });
    }
  } else if (
    result.status === 'pending' &&
    before?.status !== 'pending'
  ) {
    void publishInvoicePending({
      invoiceId: result.id,
      status: result.status,
      total: result.total,
      currency: result.currency,
    });
  }
  return result;
  }, 'invoice');
}

export async function cancelInvoice(id: string): Promise<Invoice> {
  const invoice = await getInvoice(id);
  if (!invoice) throw new Error('Factura no encontrada.');
  if (invoice.status === 'cancelled') return invoice;
  return saveInvoice(
    {
      commercialOperationId: invoice.commercialOperationId,
      clientId: invoice.clientId,
      type: invoice.type,
      status: 'cancelled',
      issueDate: invoice.issueDate,
      dueDate: invoice.dueDate,
      subtotal: invoice.subtotal,
      taxes: invoice.taxes,
      total: invoice.total,
      currency: invoice.currency,
      notes: invoice.notes,
    },
    invoice.createdBy,
    id,
  );
}

export async function createInvoiceFromOperation(
  commercialOperationId: string,
  createdBy: string,
): Promise<Invoice> {
  const operation = await getCommercialOperation(commercialOperationId);
  if (!operation) throw new Error('Operación comercial no encontrada.');
  if (!BILLABLE_OPERATION_STATUSES.includes(operation.status)) {
    throw new Error(
      'La operación debe estar aprobada, en curso o completada para facturar.',
    );
  }
  const input = buildInvoiceInputFromOperation(operation);
  return saveInvoice(input, createdBy);
}
